import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dig from 'object-dig';
import clsx from 'clsx';
import { Doughnut } from 'react-chartjs-2';

import { makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import FaceIcon from '@material-ui/icons/Face';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';
import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import LinearProgress from '~/components/LinearProgress';
import InputControl from '~/components/Input/InputControl';
import InputSelect from '~/components/Input/InputSelect';
import Pagination from '~/components/Table/Pagination';
import CardMini from '~/components/Card/CardMini';
import InputSwitch from '~/components/Input/InputSwitch';
import InputDate from '~/components/Input/InputDate';
import InputMultiCorporation from '~/components/Input/InputMultiCorporation';
import {
  showSpecialty,
  showAppointmentCategories,
  showPerson,
  showMedicalAppointments,
} from '~/config/routes';

import formatNumber from '~/utils/Number/formatNumber';
import isPresent from '~/utils/isPresent';
import toInt from '~/utils/Number/toInt';
import Request from '~/providers/Request';

const useStyles = makeStyles((theme) => ({
  cell: {
    color: '#ffffff',
    backgroundColor: theme.palette.purple.light,
  },
  hidden: {
    display: 'none',
  },
}));

function ShowCorporation() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [dataInit, setDataInit] = useState(moment().startOf('day').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('day').format());

  const [doctorsOptions, setDoctorsOptions] = useState([]);
  const [attendantOptions, setAttendantsOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [specialtiesOptions, setSpecialtiesOptions] = useState([]);
  const [corporation, setCorporation] = useState([{
    corporation_id: null,
    id: null,
    name: 'Todas',
  }]);

  const [ignoreDoctors, setIgnoreDoctors] = useState([]);
  const [ignoreSchedule, setIgnoreSchedule] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [categories, setCategories] = useState(null);

  const [appointments, setAppointments] = useState([]);
  const [appointmentsSlice, setAppointmentsSlice] = useState([]);
  const [total, setTotal] = useState(null);

  const [showPieAppointment, setShowPieAppointment] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 20;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const getPieAppointment = () => {
    let labels = [];
    let data = [];
    let pieColors = [];
    setShowPieAppointment(false);

    if (isPresent(total)) {
      const attendant_delay = total.attendant_delay;
      const attendant_duration = total.attendant_duration;
      const doctor_delay = total.doctor_delay;
      const doctor_duration = total.doctor_duration;

      labels = [
        `${t('dashboard.attributes.attendant_delay')}: ${moment
          .utc(attendant_delay * 1000)
          .format('HH:mm:ss')}`,
        `${t('dashboard.attributes.attendant_duration')}: ${moment
          .utc(attendant_duration * 1000)
          .format('HH:mm:ss')}`,
        `${t('dashboard.attributes.doctor_delay')}: ${moment
          .utc(doctor_delay * 1000)
          .format('HH:mm:ss')}`,
        `${t('dashboard.attributes.doctor_duration')}: ${moment
          .utc(doctor_duration * 1000)
          .format('HH:mm:ss')}`,
      ];
      data = [
        attendant_delay,
        attendant_duration,
        doctor_delay,
        doctor_duration,
      ];
      pieColors = ['#40BDA7', '#336699', '#00A4FF', '#FC4B4B'];

      setShowPieAppointment(true);
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const pieOptions = {
    tooltips: {
      backgroundColor: 'rgb(255,255,255)',
      bodyFontColor: '#858796',
      borderColor: '#dddfeb',
      borderWidth: 1,
      xPadding: 15,
      yPadding: 15,
      displayColors: false,
      caretPadding: 10,
      callbacks: {
        label: function (tooltipItems, data) {
          let i;
          const label = [];
          const l = data.datasets.length;

          const total = data.datasets[0].data.reduce(
            (a, b) => Number(a) + Number(b),
            0
          );
          for (i = 0; i < l; i += 1) {
            const platform = data.labels[tooltipItems.index].split(':')[0];
            const value = data.datasets[0].data[tooltipItems.index];

            let percent = (value / total) * 100;
            percent = formatNumber(percent);

            label[i] = `${platform}: ${percent}%`;
          }
          return label;
        },
      },
    },
    legend: {
      position: 'right',
      display: true,
      labels: {
        fontSize: 16,
      },
    },
    cutoutPercentage: 60,
  };

  async function fetchAppointmentsTime() {
    setLoading(true);

    let doctorsIds = [];
    let ignoreDoctorsIds = [];
    let attendantsIds = [];
    let specialtiesIds = [];
    let categoriesIds = [];

    if (isPresent(doctors)) {
      doctorsIds = doctors.map((item) => item.id);
    }

    if (isPresent(ignoreDoctors)) {
      ignoreDoctorsIds = ignoreDoctors.map((item) => item.id);
    }

    if (isPresent(attendants)) {
      attendantsIds = attendants.map((item) => item.id);
    }

    if (isPresent(specialties)) {
      specialtiesIds = specialties.map((item) => item.id);
    }

    if (isPresent(categories)) {
      categoriesIds = categories.map((item) => item.id);
    }

    const corpsId = corporation
      ? `[${corporation.map((corp) => corp.id).join(',')}]`
      : null;
    const corpsCorporationId = corporation
      ? `[${corporation.map((corp) => corp.corporation_id).join(',')}]`
      : null;

    const filters = {
      start: dataInit,
      end: dataEnd,
      ignore_schedule: ignoreSchedule,
      doctorsIds,
      ignoreDoctorsIds,
      attendantsIds,
      specialtiesIds,
      categoriesIds,
      partners: corpsId,
      corporations: corpsCorporationId,
    };
    Request.get('/admin/dashboard/appointments_time', filters, {
      normalizeJSON: false,
    })
      .then((response) => {
        setLoading(false);
        setAppointments(response.appointments);
        setTotal(response.total);
        setTotalPages(toInt(response.appointments.length / rowsPerPage));
        setPage(1);
      })
      .catch((e) => {
        setLoading(false);
        setPage(1);
        console.log(e);
      });
  }

  async function fetchDoctors() {
    Request.get('/admin/users_doctors', null, { normalizeJSON: true })
      .then((response) => {
        setDoctorsOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setDoctorsOptions([]);
      });
  }

  async function fetchAttendants() {
    Request.get('/admin/users_attendants', null, { normalizeJSON: true }).then(
      (response) => {
        setAttendantsOptions(response.data);
      }
    );
  }

  async function fetchCategories() {
    Request.get('/admin/appointment_categories', null, { normalizeJSON: true })
      .then((response) => {
        setCategoriesOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setCategoriesOptions([]);
      });
  }

  async function fetchSpecialties() {
    Request.get('/admin/specialties', null, { normalizeJSON: true })
      .then((response) => {
        setSpecialtiesOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setSpecialtiesOptions([]);
      });
  }

  useEffect(() => {
    fetchAppointmentsTime();
  }, [
    dataInit,
    dataEnd,
    doctors,
    attendants,
    ignoreDoctors,
    specialties,
    categories,
    ignoreSchedule,
    corporation,
  ]);

  useEffect(() => {
    fetchDoctors();
    fetchAttendants();
    fetchSpecialties();
    fetchCategories();
  }, []);

  useEffect(() => {
    setAppointmentsSlice(
      appointments.slice(rowsPerPage * page - rowsPerPage, rowsPerPage * page)
    );
  }, [page, appointments]);

  return (
    <>
      <Paper
        Icon={LocalHospitalIcon}
        iconColor="blue"
        title={t('dashboard.tabs.appointmentsTime')}
        loading={false}
      >
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('dashboard.attributes.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('dashboard.attributes.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputMultiCorporation corporation={corporation} setCorporation={setCorporation}/>
          </GridItem>
          <GridItem>
            <InputSwitch
              label="Desconsiderar agendamentos"
              value={ignoreSchedule}
              onChange={setIgnoreSchedule}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'doctors'}
              label={t('dashboard.attributes.doctor')}
              value={doctors}
              inputComponent={InputSelect}
              onChange={({ doctors }) => {
                setDoctors(doctors);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: doctorsOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'doctors'}
              label={t('dashboard.attributes.ignore_doctor')}
              value={ignoreDoctors}
              inputComponent={InputSelect}
              onChange={({ doctors }) => {
                setIgnoreDoctors(doctors);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: doctorsOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'attendants'}
              label={t('dashboard.attributes.attendant')}
              value={attendants}
              inputComponent={InputSelect}
              onChange={({ attendants }) => {
                setAttendants(attendants);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: attendantOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'specialties'}
              label={t('dashboard.attributes.specialty')}
              value={specialties}
              inputComponent={InputSelect}
              onChange={({ specialties }) => {
                setSpecialties(specialties);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: specialtiesOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'categories'}
              label={t('dashboard.attributes.category')}
              value={categories}
              inputComponent={InputSelect}
              onChange={({ categories }) => {
                setCategories(categories);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: categoriesOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <div className={clsx(!showPieAppointment && classes.hidden)}>
          <GridContainer justify="center" alignItems="center">
            <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
              <Typography variant="h6">
                {t('dashboard.attributes.medical_appointments_time')}
              </Typography>
              <Doughnut data={getPieAppointment} options={pieOptions} />
            </GridItem>
            <GridItem xs={12} sm={6} md={4} lg={4} xl={4}>
              <CardMini
                Icon={AccessTimeIcon}
                iconColor="green"
                text={t('dashboard.attributes.appointments_duration_total')}
                value={
                  total
                    ? moment.utc(total.total_duration * 1000).format('HH:mm:ss')
                    : null
                }
                loading={loading}
              />
            </GridItem>
          </GridContainer>
        </div>

        <LinearProgress loading={loading} />

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography variant="subtitle2" color="primary">
              <strong>{t('dashboard.attributes.count_appointments')}: </strong>
              {appointments.length}
            </Typography>
            <Table
              emptyData={appointmentsSlice.length === 0}
              loading={loading}
              columns={
                <>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.appointment')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.created_at')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.attendant_delay')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.attendant_init_at')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.attendant_duration')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.attendant_end_at')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.doctor_delay')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.doctor_init_at')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.doctor_duration')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.doctor_end_at')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.total_duration')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.doctor')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.attendant')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.user_name')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.category')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.specialty')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('dashboard.attributes.schedule_appointment_at')}
                  </TableCell>
                </>
              }
            >
              {appointmentsSlice.map((row) => {
                return (
                  <TableRow key={row.id} hover className={classesTable.row}>
                    <TableCell>
                      <Chip
                        label={row.id}
                        component={LinkMaterial}
                        href={showMedicalAppointments.reverse({
                          id: row.id,
                        })}
                        style={{ cursor: 'pointer' }}
                        target="_blank"
                        color="primary"
                        variant="outlined"
                        onDelete={() => {}}
                        deleteIcon={<OpenInNewIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      {row.created_at
                        ? moment(row.created_at).format('DD/MM/YYYY HH:mm:ss')
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.attendant_delay
                        ? moment
                            .utc(row.attendant_delay * 1000)
                            .format('HH:mm:ss')
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.attendant_init_at
                        ? moment(row.attendant_init_at).format(
                            'DD/MM/YYYY HH:mm:ss'
                          )
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.attendant_duration
                        ? moment
                            .utc(row.attendant_duration * 1000)
                            .format('HH:mm:ss')
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.attendant_end_at
                        ? moment(row.attendant_end_at).format(
                            'DD/MM/YYYY HH:mm:ss'
                          )
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.doctor_delay
                        ? moment.utc(row.doctor_delay * 1000).format('HH:mm:ss')
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.doctor_init_at
                        ? moment(row.doctor_init_at).format(
                            'DD/MM/YYYY HH:mm:ss'
                          )
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.doctor_duration
                        ? moment
                            .utc(row.doctor_duration * 1000)
                            .format('HH:mm:ss')
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.doctor_end_at
                        ? moment(row.doctor_end_at).format(
                            'DD/MM/YYYY HH:mm:ss'
                          )
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.total_duration
                        ? moment
                            .utc(row.total_duration * 1000)
                            .format('HH:mm:ss')
                        : null}
                    </TableCell>
                    <TableCell>
                      {row.doctor ? (
                        <Chip
                          icon={<FaceIcon />}
                          label={dig(row, 'doctor', 'name')}
                          component={LinkMaterial}
                          href={showPerson.reverse({
                            id: dig(row, 'doctor', 'id'),
                          })}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          color="primary"
                          variant="outlined"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.attendant ? (
                        <Chip
                          icon={<FaceIcon />}
                          label={dig(row, 'attendant', 'name')}
                          component={LinkMaterial}
                          href={showPerson.reverse({
                            id: dig(row, 'attendant', 'id'),
                          })}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          color="primary"
                          variant="outlined"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.user ? (
                        <Chip
                          icon={<FaceIcon />}
                          label={dig(row, 'user', 'name')}
                          component={LinkMaterial}
                          href={showPerson.reverse({
                            id: dig(row, 'user', 'id'),
                          })}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          color="primary"
                          variant="outlined"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.category ? (
                        <Chip
                          label={dig(row, 'category', 'name')}
                          component={LinkMaterial}
                          href={showAppointmentCategories.reverse({
                            id: dig(row, 'category', 'id'),
                          })}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          color="secondary"
                          variant="outlined"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.specialty ? (
                        <Chip
                          label={dig(row, 'specialty', 'name')}
                          component={LinkMaterial}
                          href={showSpecialty.reverse({
                            id: dig(row, 'specialty', 'id'),
                          })}
                          style={{ cursor: 'pointer' }}
                          target="_blank"
                          color="secondary"
                          variant="outlined"
                          onDelete={() => {}}
                          deleteIcon={<OpenInNewIcon />}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {row.schedule_appointment_at
                        ? moment(row.schedule_appointment_at).format(
                            'DD/MM/YYYY HH:mm:ss'
                          )
                        : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Pagination
              count={totalPages}
              page={page}
              onChangePage={handleChangePage}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />
      </Paper>
    </>
  );
}

export default ShowCorporation;
